/**
 * jarvis-today PH-02: the words Today may show (the screen's copy of the console's server/today_words.js, so an
 * older server's words cannot put a code on screen). Ruling (a), business lead -10751 Oct 28: no access code, gate
 * code or password-shaped text ever renders on Today, and a reason line is a reason of at most about 120 characters,
 * never the agent's brief. Text is never cut token by token (lesson L-0660): a line that is too long, more than one
 * sentence, or carries a code is replaced whole.
 */

export const REASON_MAX = 120;
export const GUARD_LINE = 'Has an access code: open the record to read it.';

const CODE_VALUE = new RegExp(
  '(?<!\\b(?:zip|area|postal|country|status|error|exit|source|qr|promo|discount|indiana|municipal|city|tax|fire'
  + '|zoning|penal|civil|ordinance|building|ic)\\s)'
  + '\\b(?:code|combo|combination|pin|passcode|password|pw|pwd)\\b\\s*(?:is\\s+|was\\s+|=\\s*|:\\s*|#\\s*|-\\s*)?'
  + '["\'\\u201c]?[#*]?(?=[\\w#*-]*\\d)[\\w#*-]{3,16}', 'i');
const PASSWORD = /\b(?:password|passcode|pwd?)\s*(?:is|=|:)\s*\S{3,}/i;
const ACCESS = /\b(?:lock\s*-?\s*box(?:es)?|gate|garage|door|alarm|access|entry|key\s*-?\s*pad|supra|cbs|combo)\s+(?:code|combo|combination|pin|passcode|password)s?\b/i;
const ABBREVIATION = /(?:^|\s)(?:[A-Za-z]|St|Mr|Mrs|Ms|Dr|Ave|Rd|Ln|Ct|Blvd|No|Jr|Sr|Inc|Co|Mt|Ft|vs|etc|approx)\.$/;

export function hasAccessCode(value: unknown): boolean {
  return typeof value === 'string' && (CODE_VALUE.test(value) || PASSWORD.test(value) || ACCESS.test(value));
}

function sentences(value: string): number {
  let count = 1;
  const boundary = /[.!?]+\s+(?=["'“(]?[A-Z0-9])/g;
  let match: RegExpExecArray | null;
  while ((match = boundary.exec(value)) !== null) {
    if (!ABBREVIATION.test(value.slice(0, match.index + 1))) count += 1;
  }
  return count;
}

/** The text as a reason (whitespace folded) when short, one sentence and code-free; else the fallback, never cut. */
export function reasonText<T extends string | null>(value: unknown, fallback: T): string | T {
  if (typeof value !== 'string') return fallback;
  const cleaned = value.replace(/\s+/g, ' ').trim();
  if (!cleaned) return fallback;
  if (cleaned.length > REASON_MAX || /[\n•]/.test(value) || sentences(cleaned) > 1 || hasAccessCode(cleaned)) {
    return fallback;
  }
  return cleaned;
}

/** Every string in a response that still carries a code becomes the fixed line (a copy; the input is kept). */
export function guardBody<T>(value: T): T {
  if (typeof value === 'string') return (hasAccessCode(value) ? GUARD_LINE : value) as T;
  if (Array.isArray(value)) return value.map((item) => guardBody(item)) as T;
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) out[key] = guardBody(item);
    return out as T;
  }
  return value;
}

/**
 * A due need's "Name: what to do". What to do is a short reason; a call whose task is too long or carries a code keeps
 * its first part ("Call Ann Smith") so Next up still finds it; a part is never written twice ("Follow-up: Follow-up").
 */
export function dueLabel(label: string): string {
  const split = label.indexOf(': ');
  const subject = split > 0 ? label.slice(0, split) : null;
  const rest = split > 0 ? label.slice(split + 2) : label;
  let task = reasonText(rest, null);
  if (task === null && /^Call\s/.test(rest)) task = reasonText(rest.split(':')[0], null);
  if (subject === null) return task ?? (hasAccessCode(rest) ? GUARD_LINE : 'A task is due');
  return task === null || task === subject ? subject : `${subject}: ${task}`;
}
